import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const LOGOS_BASE = '/img/logos';
const LOGOS_MANIFEST_URL = '/img/logos-manifest.json';

function logoUrl(filename) {
  return `${LOGOS_BASE}/${encodeURIComponent(filename)}`;
}

function LogoPreview() {
  const [logoFiles, setLogoFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLogo, setSelectedLogo] = useState(null);

  useEffect(() => {
    fetch(LOGOS_MANIFEST_URL)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Manifest not found'))))
      .then((files) => {
        setLogoFiles(files);
        setSelectedLogo(files.length > 0 ? files[0] : null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const logoSrc = selectedLogo ? logoUrl(selectedLogo) : null;

  return (
    <div className="min-h-screen bg-gray-100 font-ui">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Logo Preview</h1>
            <p className="text-gray-600 mt-1">Compare how different logos look on the homepage and login scenarios</p>
          </div>
          <Link
            to="/"
            className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Back to Home
          </Link>
        </div>

        {/* Logo candidates grid: click to select current — all images from /img/logos */}
        <section className="mb-12">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Logos in folder (Click to select)</h2>
          {loading && <p className="text-gray-500">Loading logos…</p>}
          {error && (
            <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Could not load logo list. Run <code className="bg-amber-100 px-1 rounded">node scripts/generate-logos-manifest.js</code> in the frontend directory to generate the list.
            </p>
          )}
          {!loading && !error && logoFiles.length === 0 && (
            <p className="text-gray-500">No images in the logo folder. Add images to <code className="bg-gray-200 px-1 rounded">public/img/logos</code> and run the manifest script.</p>
          )}
          {!loading && logoFiles.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
              {logoFiles.map((filename) => (
                <button
                  key={filename}
                  type="button"
                  onClick={() => setSelectedLogo(filename)}
                  className={`rounded-lg border-2 p-3 bg-white transition-all ${
                    selectedLogo === filename
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src={logoUrl(filename)}
                    alt={filename}
                    className="w-full h-14 object-contain object-center"
                  />
                  <p className="text-xs text-gray-500 mt-2 truncate" title={filename}>{filename}</p>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Home page: Nav + Banner (same structure as real home page) */}
        {logoSrc && (
        <section className="mb-12">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Scenario: Home Page (Nav + Banner)</h2>
          <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-[#F3F3F3]">
            {/* Nav bar — matches MainLayout + Navigation */}
            <nav className="bg-white border-b border-gray-200">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                  <div className="flex-shrink-0">
                    <span className="flex items-center">
                      <img
                        src={logoSrc}
                        alt="AmpTrans logo"
                        className="h-10 w-auto max-h-10"
                        style={{ maxHeight: '40px' }}
                      />
                    </span>
                  </div>
                  <div className="hidden md:flex items-center space-x-4 text-sm text-gray-500">
                    About · Solutions · Login
                  </div>
                </div>
              </div>
            </nav>
            {/* Banner — matches Header (gradient, headline, CTA, video area) */}
            <div className="py-2 relative">
              <div className="bg-gradient-to-br from-blue-900 via-blue-500 to-blue-300 text-white py-12 lg:py-20 relative overflow-hidden">
                {/* Video (right side, same clip as header.css) */}
                <div
                  className="absolute top-0 right-0 w-1/2 h-full overflow-hidden"
                  style={{ clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0 100%)' }}
                >
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto object-cover -translate-x-1/2 -translate-y-1/2"
                    style={{ zIndex: 1 }}
                  >
                    <source src="/videos/traffic-1.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute inset-0 bg-black/20 pointer-events-none" style={{ zIndex: 2 }} />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 px-6 lg:px-8">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-12">
                    <div className="w-full md:w-1/2 text-center md:text-left pl-3 md:pl-8 lg:pl-12">
                      <h1 className="font-ui text-3xl md:text-4xl font-bold mb-4 leading-tight">
                        Planning today.<br />
                        Powering tomorrow.
                      </h1>
                      <p className="text-blue-100 text-xl mb-8 leading-relaxed font-ui">
                        Analytics and strategic solutions to make every mile smarter.
                      </p>
                      <div className="pt-4">
                        <span className="inline-block px-4 py-2 rounded-lg bg-white text-blue-700 text-sm font-medium">
                          Sign up now
                        </span>
                      </div>
                    </div>
                    <div className="w-full md:w-1/2" aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* Current selection: Post-login homepage header style */}
        {logoSrc && (
        <section className="mb-12">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Scenario: Post-login Homepage Header</h2>
          <div className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
            <header className="bg-white shadow-sm border-b border-gray-200">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <img
                      src={logoSrc}
                      alt="AmpTrans Logo"
                      className="h-10 w-auto"
                    />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Welcome back!</h3>
                      <p className="text-sm text-gray-600">Choose a tool to get started</p>
                    </div>
                  </div>
                </div>
              </div>
            </header>
          </div>
        </section>
        )}

        {/* Current selection: Main navigation bar style */}
        {logoSrc && (
        <section className="mb-12">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Scenario: Main Navigation Bar</h2>
          <div className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm">
            <nav className="bg-white border-b border-gray-200">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                  <div className="flex-shrink-0">
                    <span className="flex items-center">
                      <img
                        src={logoSrc}
                        alt="AmpTrans logo"
                        className="h-10 w-auto max-h-10"
                        style={{ maxHeight: '40px' }}
                      />
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">About · Solutions · …</div>
                </div>
              </div>
            </nav>
          </div>
        </section>
        )}

        {/* Current selection: Login page top left */}
        {logoSrc && (
        <section className="mb-12">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Scenario: Login Page Top Left</h2>
          <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm relative min-h-[200px] bg-gray-50">
            <div className="absolute top-6 left-6 z-10">
              <span className="flex items-center">
                <img src={logoSrc} alt="AmpTrans Logo" className="w-auto h-12" />
              </span>
            </div>
            <div className="flex-1 flex items-center justify-center px-4 py-8 min-h-[200px]">
              <div className="w-full max-w-sm h-24 rounded-lg border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-sm">
                Login form placeholder
              </div>
            </div>
          </div>
        </section>
        )}

        {/* Different backgrounds and sizes */}
        {logoSrc && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Backgrounds and Sizes</h2>
          <div className="space-y-6">
            {[
              { name: 'White', className: 'bg-white' },
              { name: 'Dark', className: 'bg-gray-800' },
              { name: 'Gradient', className: 'bg-gradient-to-br from-gray-50 to-gray-100' },
            ].map((bg) => (
              <div key={bg.name} className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                <p className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 border-b border-gray-200">
                  {bg.name}
                </p>
                <div className={`px-4 py-6 flex flex-wrap items-end gap-8 ${bg.className}`}>
                  {[24, 32, 48, 64].map((h) => (
                    <div key={h} className="flex flex-col items-center gap-2">
                      <img
                        src={logoSrc}
                        alt=""
                        className="w-auto object-contain"
                        style={{ height: `${h}px` }}
                      />
                      <span className="text-xs text-gray-500">{h}px</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
        )}
      </div>
    </div>
  );
}

export default LogoPreview;
